import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CareerGoals = ({ onNext, onPrevious, onSave, savedData = {} }) => {
  const [selectedGoals, setSelectedGoals] = useState(savedData?.goals || []);
  const [draggedItem, setDraggedItem] = useState(null);

  const availableGoals = [
    {
      id: 'salary_increase',
      title: 'Increase Salary',
      description: 'Achieve higher compensation and financial growth',
      icon: 'TrendingUp'
    },
    {
      id: 'career_advancement',
      title: 'Career Advancement',
      description: 'Move to senior or leadership positions',
      icon: 'ArrowUp'
    },
    {
      id: 'skill_development',
      title: 'Skill Development',
      description: 'Learn new technologies and improve expertise',
      icon: 'BookOpen'
    },
    {
      id: 'work_life_balance',
      title: 'Work-Life Balance',
      description: 'Find better balance between work and personal life',
      icon: 'Scale'
    },
    {
      id: 'remote_work',
      title: 'Remote Work',
      description: 'Secure remote or flexible work arrangements',
      icon: 'Home'
    },
    {
      id: 'industry_change',
      title: 'Industry Change',
      description: 'Transition to a different industry or field',
      icon: 'RefreshCw'
    },
    {
      id: 'entrepreneurship',
      title: 'Entrepreneurship',
      description: 'Start your own business or become self-employed',
      icon: 'Lightbulb'
    },
    {
      id: 'job_security',
      title: 'Job Security',
      description: 'Find stable, long-term employment opportunities',
      icon: 'Shield'
    }
  ];

  const addGoal = (goal) => {
    if (!selectedGoals?.some(selected => selected?.id === goal?.id)) {
      setSelectedGoals([...selectedGoals, { ...goal, priority: selectedGoals?.length + 1 }]);
    }
  };

  const removeGoal = (goalId) => {
    const updatedGoals = selectedGoals?.filter(goal => goal?.id !== goalId)?.map((goal, index) => ({ ...goal, priority: index + 1 }));
    setSelectedGoals(updatedGoals);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e?.preventDefault();
    if (draggedItem === null) return;

    const newGoals = [...selectedGoals];
    const draggedGoal = newGoals?.[draggedItem];
    
    newGoals?.splice(draggedItem, 1);
    newGoals?.splice(dropIndex, 0, draggedGoal);
    
    const reorderedGoals = newGoals?.map((goal, index) => ({
      ...goal,
      priority: index + 1
    }));
    
    setSelectedGoals(reorderedGoals);
    setDraggedItem(null);
  };

  const handleNext = () => {
    const data = { goals: selectedGoals };
    onSave(data);
    onNext();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-text-primary mb-2">Career Goals</h3>
        <p className="text-text-secondary">
          Select your career objectives and prioritize them by dragging to reorder. Your top priorities will influence job recommendations.
        </p>
      </div>
      {/* Available Goals */}
      <div className="mb-6">
        <h4 className="text-lg font-medium text-text-primary mb-4">Available Goals</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {availableGoals?.filter(goal => !selectedGoals?.some(selected => selected?.id === goal?.id))?.map((goal) => (
              <button
                key={goal?.id}
                onClick={() => addGoal(goal)}
                className="p-4 border border-border rounded-lg text-left hover:border-primary hover:bg-primary/5 spring-transition"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={goal?.icon} size={20} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-text-primary mb-1">{goal?.title}</h5>
                    <p className="text-sm text-text-secondary">{goal?.description}</p>
                  </div>
                  <Icon name="Plus" size={16} className="text-text-secondary" />
                </div>
              </button>
            ))}
        </div>
      </div>
      {/* Selected Goals */}
      <div className="mb-6">
        <h4 className="text-lg font-medium text-text-primary mb-4">
          Your Priorities ({selectedGoals?.length}/5)
        </h4>
        
        {selectedGoals?.length === 0 ? (
          <div className="text-center py-8 text-text-secondary">
            <Icon name="Target" size={48} className="mx-auto mb-4 opacity-50" />
            <p>No goals selected yet. Choose your career objectives from above.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-text-secondary mb-3 flex items-center space-x-2">
              <Icon name="Info" size={16} />
              <span>Drag to reorder by priority (top = highest priority)</span>
            </div>
            
            {selectedGoals?.map((goal, index) => (
              <div
                key={goal?.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className={`p-4 border border-border rounded-lg bg-background cursor-move hover:border-primary/50 spring-transition ${
                  draggedItem === index ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                      {goal?.priority}
                    </div>
                    <Icon name="GripVertical" size={16} className="text-text-secondary" />
                  </div>
                  
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={goal?.icon} size={20} className="text-primary" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-text-primary">{goal?.title}</h5>
                    <p className="text-sm text-text-secondary">{goal?.description}</p>
                  </div>
                  
                  <button
                    onClick={() => removeGoal(goal?.id)}
                    className="text-destructive hover:text-destructive/80 spring-transition"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Goal Insights */}
      {selectedGoals?.length > 0 && (
        <div className="mb-6 p-4 bg-muted rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Lightbulb" size={20} className="text-warning flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="font-medium text-text-primary mb-2">Goal Insights</h5>
              <p className="text-sm text-text-secondary">
                Based on your priorities, we'll focus on opportunities that align with{' '}
                <span className="font-medium text-text-primary">
                  {selectedGoals?.[0]?.title?.toLowerCase()}
                </span>
                {selectedGoals?.length > 1 && (
                  <span> and {selectedGoals?.[1]?.title?.toLowerCase()}</span>
                )}
                . You can always update these preferences later.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-border">
        <Button variant="outline" onClick={onPrevious} iconName="ArrowLeft">
          Previous
        </Button>
        <Button
          onClick={handleNext}
          disabled={selectedGoals?.length === 0}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Experience
        </Button>
      </div>
    </div>
  );
};

export default CareerGoals;